import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, IconButton, Tooltip, Checkbox, Chip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { getNotificationStyle } from '../../utils/notificationStyle';
import NotificationStatusChip from '../NotificationStatusChip/NotificationStatusChip';
import { notificationDisplayIcon } from '../../utils/notificationIcons';
import { formatRelativeTime, formatAlarmDuration } from '../../utils/notificationTime';

/**
 * Shared notification row component used in both the full Notifications page
 * list and the Notification Bell popover.
 *
 * Spec §3.5 / §3.6 / §3.12: A custom Paper-based component is used instead of
 * Brightlayer UI InfoListItem (deliberate deviation). InfoListItem does not
 * easily accommodate the inline NotificationStatusChip and the independently
 * stoppable clear (delete) button. Any future migration to InfoListItem must
 * preserve these features.
 *
 * Row layout per §3.5 / §3.6:
 *   Line 1 (subtitle): `{siteId} · {notificationType} · {severity}`
 *   Line 2 (time row): `🕓 {relativeTime}`  optionally followed by
 *                      `· {duration} ⏱` for alarms that have returned to
 *                      normal (i.e. `returnedToNormalAt` is set).
 *
 * Props:
 *   notification     – mapped notification object
 *   onClick          – called when the row is clicked
 *   onClear          – optional; when provided a delete (clear) button is shown
 *   selectable       – when truthy, render a leading selection checkbox
 *   selected         – checkbox state (controlled)
 *   onToggleSelect   – called with `(notification, { shiftKey, ctrlKey, metaKey })`
 *                      when the checkbox toggles. Click propagation is stopped so
 *                      the row's navigation `onClick` does not also fire. The
 *                      modifier flags let the parent implement shift-click range
 *                      select and ctrl/cmd-click toggle without each row needing
 *                      to know the anchor index.
 *   selectAriaLabel  – optional aria-label for the checkbox (defaults to
 *                      "Select {title}")
 */
const NotificationListItem = ({
    notification: n,
    onClick,
    onClear,
    selectable = false,
    selected = false,
    onToggleSelect,
    selectAriaLabel,
    'data-testid': testId,
}) => {
    const { t } = useTranslation();
    const style = getNotificationStyle(n);
    const subtitleParts = [n.displaySiteId, n.notificationType, n.severity].filter(Boolean);
    const relativeTime = formatRelativeTime(n.raisedAt);
    const showDuration = n.notificationType === 'alarm' && Boolean(n.returnedToNormalAt);
    const duration = showDuration ? formatAlarmDuration(n.raisedAt, n.returnedToNormalAt) : '';

    return (
        <Paper
            data-testid={testId || `notif-row-${n.id}`}
            elevation={0}
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                mb: 1,
                cursor: 'pointer',
                bgcolor: style.tintBg || 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `5px solid ${style.iconColor}`,
                '&:hover': { filter: 'brightness(0.97)' },
            }}
        >
            {selectable && (
                <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => { /* handled in onClick to capture modifier keys */ }}
                    onClick={(e) => {
                        // Stop click from bubbling so the row's `onClick` handler
                        // does not also fire when toggling the checkbox. We read
                        // modifier keys from the click MouseEvent (the bubbled
                        // change event does not carry them) so the parent can
                        // implement shift-click range select and ctrl/cmd-click
                        // toggle.
                        e.stopPropagation();
                        if (onToggleSelect) {
                            onToggleSelect(n, {
                                shiftKey: e.shiftKey,
                                ctrlKey: e.ctrlKey,
                                metaKey: e.metaKey,
                            });
                        }
                    }}
                    inputProps={{
                        'aria-label': selectAriaLabel || `Select ${n.title || ''}`,
                        'data-testid': `notif-select-${n.id}`,
                    }}
                />
            )}
            <Box sx={{
                color: style.iconColor,
                display: 'flex',
                animation: style.pulse ? 'blui-pulse 1.5s infinite' : 'none',
            }}>
                {notificationDisplayIcon(n)}
            </Box>

            <Box flex={1} sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    fontWeight={style.titleWeight}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {n.title}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                >
                    {subtitleParts.join(' · ')}
                </Typography>

                {n.alarmType && (
                    <Chip size="small" variant="outlined" label={t(`notifications.alarmTypes.${n.alarmType}`, { defaultValue: n.alarmType })} data-testid="alarm-type-badge" />
                )}
                {relativeTime && (
                    <Box
                        data-testid="notif-time-row"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.25,
                            color: 'text.secondary',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <ScheduleIcon
                            data-testid="notif-time-icon"
                            aria-label="Raised at"
                            sx={{ fontSize: '0.875rem', color: 'text.disabled', flexShrink: 0 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {relativeTime}
                        </Typography>
                        {showDuration && duration && (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mx: 0.25 }}
                                >
                                    ·
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    data-testid="notif-duration-text"
                                >
                                    {duration}
                                </Typography>
                                <TimerOutlinedIcon
                                    data-testid="notif-duration-icon"
                                    aria-label="Alarm duration"
                                    sx={{ fontSize: '0.875rem', color: 'text.disabled', flexShrink: 0 }}
                                />
                            </>
                        )}
                    </Box>
                )}
            </Box>

            <NotificationStatusChip notification={n} />

            {onClear && (
                <Tooltip title="Clear">
                    <IconButton
                        size="small"
                        aria-label={`Clear ${n.title}`}
                        onClick={(e) => { e.stopPropagation(); onClear(n, e); }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            <NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </Paper>
    );
};

export default NotificationListItem;
